import { useState } from 'react';
import { useLocation } from 'react-router';
import classNames from 'classnames';

import { CurrencyEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { formatCurrencyBySymbol, getCurrencySymbolByCurrencyCode } from '../../lib/util/DataHelpers';

import CopyImage from '../../assets/img/copy-icon.svg';
import InfoCircle from '../../assets/img/info-circle-orange.svg';
import { ClickableImage, Toast } from '../../components';
import GoBackButton from '../../components/GoBackButton/GoBackButton';
import LogoAndCloseButton from '../../components/LogoAndCloseButton/LogoAndCloseButton';
import { useCopiedToast, useIsMobile } from '../../hooks';
import Button from '../../shared_components/Button/Button';

import CryptoPaymentModal from './CryptoPaymentModal/CryptoPaymentModal';
import { CryptoPaymentState } from './CryptoPayment.types';

import styles from './CryptoPayment.module.scss';

const truncateAddress = (address: string): string => {
  if (!address || address.length <= 14) return address;
  return `${address.slice(0, 6)}...${address.slice(-6)}`;
};

const CryptoPayment = (): JSX.Element => {
  const location = useLocation();
  const { isMobile, isTablet } = useIsMobile();
  const { isCopied, copyToClipboard } = useCopiedToast();
  const [showModal, setShowModal] = useState<boolean>(false);

  const state = location.state as CryptoPaymentState;
  const isResetDailyLossPayment = state?.isResetDailyLossPayment ?? false;
  const isDeactivationOfAutoPass = state?.isDeactivationOfAutoPass ?? false;
  const isPlanPurchase = !!state?.selectedPlan;

  // Plan data:
  const selectedPlan = state?.selectedPlan;
  const withAutoPass = state?.withAutoPass;
  const accountSize = selectedPlan?.daily_hard_bp ?? '';
  const maxDrawdown = selectedPlan?.maximum_total_loss ?? 0;
  const maxDrawdownAmount = selectedPlan?.maximum_total_loss_amount ?? 0;
  const planPrice = withAutoPass
    ? selectedPlan?.plan_price_with_auto_pass
    : selectedPlan?.plan_price;

  const couponResult = state?.couponResult;
  const isCouponApplied = !!couponResult;

  // Payment data:
  const rawData = state?.raw_data;
  const paymentLink = rawData?.payment_uri ?? '';
  const walletAddress = rawData?.crypto_wallet ?? '';
  const amountUsd = rawData?.amount ?? 0;
  const amountUsdt = rawData?.crypto_amount ?? 0;
  const memoReference = state?.payment_reference ?? '';
  const qrCodeUrl = rawData?.qr_code ?? '';
  const rate = rawData?.rate ?? 0;
  const currency = rawData?.currency ?? CurrencyEnum.USD;
  const currencySymbol = getCurrencySymbolByCurrencyCode(currency);

  const isDesktop = !isMobile && !isTablet;

  const handleContinue = () => {
    setShowModal(true);
  };

  const commonContent = () => (
    <>
      <div className={styles.commonContentTitle}>
        {isResetDailyLossPayment
          ? lang.cryptoPaymentDailyLossRewindTitle()
          : lang.cryptoPaymentDailyDeactivateAutoScaleTitle()}
      </div>
      <div className={styles.commonContentContainer}>
        <div>{lang.cryptoPaymentTotalPrice()}</div>
        <div>{`${currencySymbol}${amountUsd}`}</div>
      </div>
    </>
  );

  const selectedPlanContent = () => (
    <>
      <div className={styles.selectedPlanTitle}>{lang.cryptoPaymentSelectedPlanTitle()}</div>
      <div className={styles.planDetails}>
        <div className={styles.planRow}>
          <div className={styles.planLabel}>{lang.accountSize()}</div>
          <div className={styles.planValue}>
            {currencySymbol}
            {accountSize.toLocaleString()}
          </div>
        </div>
        <div className={styles.planRow}>
          <div className={styles.planLabel}>{lang.maxDrawdown()}</div>
          <div className={styles.planValue}>
            {maxDrawdown}
            {'% ('}
            {currencySymbol}
            {maxDrawdownAmount.toLocaleString()}
            {')'}
          </div>
        </div>
        <div className={styles.planRow}>
          <div className={styles.planLabel}>{lang.autoPass()}</div>
          <div className={styles.planValue}>
            {withAutoPass
              ? lang.cryptoPaymentAutoPassActive()
              : lang.cryptoPaymentAutoPassInactive()}
          </div>
        </div>
      </div>
      <div className={styles.planPriceRow}>
        <div className={styles.planPriceLabelColumn}>
          <div className={styles.planPriceLabel}>{lang.cryptoPaymentPlanPriceLabel()}</div>
          {isCouponApplied && (
            <div className={styles.couponAppliedLabel}>
              {lang.paymentMethodSelectedPlanDiscountApplied()}
            </div>
          )}
        </div>
        <div className={styles.planPriceValueWrapper}>
          {isCouponApplied ? (
            <div className={styles.planPriceValueCouponWrapper}>
              <div className={styles.planPriceValueCoupon}>{lang.cryptoPaymentPlanPriceSuffix()}</div>
              <div className={styles.planPriceValueCouponValues}>
                <div className={styles.originalPrice}>
                  {formatCurrencyBySymbol(couponResult.original_amount, currencySymbol)}
                </div>
                <div>{formatCurrencyBySymbol(couponResult.final_amount, currencySymbol)}</div>
              </div>
            </div>
          ) : (
            <div className={styles.planPriceValue}>
              <div>{`${currencySymbol}${planPrice} ${lang.cryptoPaymentPlanPriceSuffix()}`}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className={styles.container}>
      <LogoAndCloseButton
        displayCloseButton
        newWebGoBackButtonDesign
        displayBackButton={isDesktop}
        onClose={handleContinue}
      />
      <div className={styles.wrapper}>
        <div className={styles.leftColumn}>
          {isTablet && (
            <div className={styles.tabletGoBackButton}>
              <GoBackButton />
            </div>
          )}
          <div className={styles.title}>{lang.cryptoPaymentTitle()}</div>

          <div className={styles.qrPanel}>
            <div className={styles.qrInfo}>
              <div className={styles.fieldTitle}>{lang.cryptoPaymentQuickPaymentTitle()}</div>
              <div className={styles.qrDescription}>
                {isDesktop
                  ? lang.cryptoPaymentQrDescription()
                  : lang.cryptoPaymentQrDescriptionMobile()}
              </div>
              <div className={classNames(styles.fieldSection, styles.paymentLinkSection)}>
                <div className={styles.fieldLabel}>{lang.cryptoPaymentPaymentLinkLabel()}</div>
                <div className={styles.inputRow}>
                  <div className={styles.fieldValue}>{truncateAddress(paymentLink)}</div>
                  <ClickableImage
                    className={styles.copyButton}
                    onClick={() => copyToClipboard(paymentLink)}
                    aria-label={lang.cryptoPaymentCopyPaymentLinkAriaLabel()}
                    src={CopyImage}
                  />
                </div>
              </div>
            </div>
            {qrCodeUrl && isDesktop && (
              <img
                src={`data:image/png;base64, ${qrCodeUrl}`}
                alt="qr-code"
                className={styles.qrCode}
              />
            )}
          </div>

          <div className={styles.manualPanel}>
            <div className={styles.manualTitle}>{lang.cryptoPaymentManualTitle()}</div>
            <div className={styles.manualDescription}>{lang.cryptoPaymentManualDescription()}</div>
            <div className={styles.manualFieldsWrapper}>
              <div className={styles.fieldSection}>
                <div className={styles.fieldLabel}>{lang.cryptoPaymentSendAmountLabel()}</div>
                <div className={styles.inputRow}>
                  <div className={styles.fieldValue}>
                    {amountUsdt}
                    {` ${lang.cryptoPaymentUsdtSuffix()}`}
                  </div>
                </div>
                <div className={styles.usdtRate}>{lang.cryptoPaymentUsdtRate(rate)}</div>
              </div>
              <div className={styles.fieldSection}>
                <div className={styles.fieldLabel}>{lang.cryptoPaymentToAddressLabel()}</div>
                <div className={styles.inputRow}>
                  <div className={classNames(styles.fieldValue, styles.walletFieldValue)}>
                    {truncateAddress(walletAddress)}
                  </div>
                  <ClickableImage
                    type="button"
                    className={styles.copyButton}
                    onClick={() => copyToClipboard(walletAddress)}
                    aria-label={lang.cryptoPaymentCopyAddressAriaLabel()}
                    src={CopyImage}
                  />
                </div>
              </div>
            </div>
            <div className={styles.memoRow}>
              <div className={styles.fieldLabel}>{lang.cryptoPaymentMemoLabel()}</div>
              <div className={styles.memoValue}>
                <ClickableImage
                  type="button"
                  className={styles.copyButton}
                  onClick={() => copyToClipboard(memoReference)}
                  aria-label={lang.cryptoPaymentCopyMemoAriaLabel()}
                  src={CopyImage}
                />
                <div className={styles.memoText}>{memoReference}</div>
              </div>
            </div>

            <div className={styles.infoBox}>
              <img src={InfoCircle} alt="" className={styles.infoIcon} />
              <div className={styles.infoText}>{lang.cryptoPaymentMemoInfo()}</div>
            </div>
          </div>
        </div>
        <div className={styles.rightColumn}>
          <div className={styles.rightColumnContent}>
            {selectedPlan ? selectedPlanContent() : commonContent()}
          </div>
          <Button biggerFontSize className={styles.continueButton} onClick={handleContinue}>
            {lang.cryptoPaymentConfirmPayment()}
          </Button>
          <div className={styles.qrCodeMessage}>{lang.cryptoPaymentQrCodeMessage()}</div>
        </div>
      </div>
      <Toast isOpen={isCopied} message={lang.copiedToClipboard()} />
      <CryptoPaymentModal
        isOpen={showModal}
        setIsOpen={setShowModal}
        isAutoPass={isDeactivationOfAutoPass}
        isPlanPurchase={isPlanPurchase}
      />
    </div>
  );
};

export default CryptoPayment;
